import { useCallback, useEffect, useState } from 'react';

interface UseCountdownTimerOptions {
  /** 초기 카운트다운 시간 (초) */
  initialSeconds: number;
  /** 타이머 시작 여부 */
  enabled?: boolean;
  /** 타이머 종료 시 콜백 */
  onExpire?: () => void;
  /** 이 값이 변경되면 타이머 재시작 */
  resetKey?: number;
}

interface UseCountdownTimerReturn {
  /** 현재 남은 시간 (초) */
  timer: number;
  /** 타이머 만료 여부 */
  isExpired: boolean;
  /** 포맷된 시간 문자열 (MM:SS) */
  formattedTime: string;
  /** 타이머 리셋 */
  reset: () => void;
}

/**
 * 카운트다운 타이머 훅
 *
 * @example
 * const { timer, isExpired, formattedTime, reset } = useCountdownTimer({
 *   initialSeconds: 300, // 5분
 *   enabled: codeSent,
 *   onExpire: () => console.log('Timer expired'),
 * });
 */
export const useCountdownTimer = ({
  initialSeconds,
  enabled = false,
  onExpire,
  resetKey = 0,
}: UseCountdownTimerOptions): UseCountdownTimerReturn => {
  const [timer, setTimer] = useState(initialSeconds);

  // enabled가 true가 되거나 resetKey가 변경되면 타이머 시작
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // 타이머 리셋 후 시작
    setTimer(initialSeconds);

    const interval = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled, initialSeconds, onExpire, resetKey]);

  const reset = useCallback(() => {
    setTimer(initialSeconds);
  }, [initialSeconds]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return {
    timer,
    isExpired: timer === 0,
    formattedTime: formatTime(timer),
    reset,
  };
};
